import React, { useState, useEffect, useCallback } from 'react';
import {
  HardDrive,
  Loader2,
  RefreshCw,
  AlertTriangle,
  Database,
  Layers,
  Boxes,
  Archive,
  Minimize2,
  GitMerge,
  Unlink,
  Hash,
  Gauge,
  Zap,
  Sparkles,
  Info,
  Terminal,
  Copy,
  Check,
} from 'lucide-react';
import baseapi from '../../../services/baseapi';
import ClickhouseTable from './clickhousestorage/ClickhouseTable';
import ClickhouseParts from './clickhousestorage/ClickhouseParts';
import ClickhouseMerges from './clickhousestorage/ClickhouseMerges';

export interface ClickhouseStorageProps {
  activeCluster: string;
  timeRange?: string;
  clientId?: string;
  refreshKey?: number;
  dbType?: string;
}

export interface ClickhouseStorageWarning {
  code?: string;
  severity?: string;
  message: string;
  detail_endpoint?: string | null;
}

export interface ClickhouseFindingItem {
  severity: 'critical' | 'warning' | 'info' | string;
  category: string;
  database?: string | null;
  object?: string | null;
  finding: string;
  impact: string;
  remediation: string;
  command?: string | null;
}

export interface ClickhouseFindingsData {
  note?: string;
  count?: number;
  by_severity?: Record<string, number>;
  items?: ClickhouseFindingItem[];
}

export interface ClickhouseStorageOverviewData {
  status: boolean;
  applicable: boolean;
  findings?: ClickhouseFindingsData;
  storage?: {
    disks_fresh?: boolean;
    tables_fresh?: boolean;
    total_table_bytes?: number | null;
    total_table_rows?: number | null;
    disk_used_bytes?: number | null;
    disk_total_bytes?: number | null;
    disk_used_pct?: number | null;
    disk_capacity_status?: string | null;
    host_disk_used_pct?: number | null;
    disk_free_bytes?: number | null;
    table_data_bytes?: number | null;
    size_on_disk_bytes?: number | null;
    compression_ratio?: number | string | null;
    compression?: number | string | null;
    total_data_bytes?: number | null;
  };
  parts?: {
    parts_fresh?: boolean;
    detached_parts_fresh?: boolean;
    active_parts?: number | null;
    detached_parts?: number | null;
    detached_parts_by_reason?: any;
  };
  merges?: {
    fresh?: boolean;
    running_count?: number | null;
    longest_elapsed_sec?: number | null;
  };
  mutations?: {
    fresh?: boolean;
    pending_count?: number | null;
    failed_count?: number | null;
  };
  health?: {
    status?: string;
    warnings?: ClickhouseStorageWarning[];
  };
  [key: string]: any;
}

const formatBytes = (bytes: number | undefined | null): string => {
  if (bytes === undefined || bytes === null || isNaN(bytes)) return '—';
  const b = Number(bytes);
  if (b === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const i = Math.floor(Math.log(Math.abs(b)) / Math.log(k));
  const safeI = Math.min(Math.max(i, 0), sizes.length - 1);
  const val = b / Math.pow(k, safeI);
  return `${Number(val.toFixed(1))} ${sizes[safeI]}`;
};

const formatPercent = (pct: number | undefined | null): string => {
  if (pct === undefined || pct === null || isNaN(pct)) return '—';
  return `${Number(pct).toFixed(2)}%`;
};

const formatNumber = (num: number | undefined | null): string => {
  if (num === undefined || num === null || isNaN(num)) return '—';
  return Number(num).toLocaleString();
};

const formatCompression = (storage?: ClickhouseStorageOverviewData['storage']): string => {
  if (!storage) return '—';
  const comp = storage.compression_ratio ?? storage.compression;
  if (comp !== undefined && comp !== null) {
    if (typeof comp === 'number') {
      return `${comp.toFixed(1)}x`;
    }
    const str = String(comp).trim();
    if (str.toLowerCase().endsWith('x')) return str;
    const num = parseFloat(str);
    return isNaN(num) ? str : `${num.toFixed(1)}x`;
  }
  const uncompressed = storage.total_data_bytes ?? storage.table_data_bytes;
  const compressed = storage.size_on_disk_bytes ?? storage.total_table_bytes;
  if (uncompressed && compressed && compressed > 0) {
    return `${(uncompressed / compressed).toFixed(1)}x`;
  }
  return '—';
};

export const ClickhouseStorage: React.FC<ClickhouseStorageProps> = ({
  activeCluster,
  timeRange,
  clientId,
  refreshKey,
  dbType = 'clickhouse',
}) => {
  const [data, setData] = useState<ClickhouseStorageOverviewData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string>('');
  const [copiedCommand, setCopiedCommand] = useState<string | null>(null);

  const handleCopy = (text: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedCommand(text);
    setTimeout(() => setCopiedCommand(null), 2000);
  };

  const fetchData = useCallback(async () => {
    if (!activeCluster) return;
    setLoading(true);
    setError(null);

    const minutes = parseInt(timeRange || '60', 10);
    const params: any = {
      cluster_id: activeCluster,
      minutes,
    };
    if (clientId) {
      params.client_id = clientId;
    }

    try {
      const res = await baseapi.get(
        '/db_monitor_dashboard/clickhouse/storage-maintenance/overview',
        { params }
      );
      const resData: ClickhouseStorageOverviewData = res.data?.data || res.data;
      setData(resData);
      setLastUpdated(new Date().toLocaleTimeString());
    } catch (err: any) {
      console.warn('ClickhouseStorage fetch error:', err);
      setError(
        err?.response?.data?.message || err?.message || 'Failed to load ClickHouse storage overview'
      );
    } finally {
      setLoading(false);
    }
  }, [activeCluster, timeRange, clientId]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  const storage = data?.storage;
  const parts = data?.parts;
  const merges = data?.merges;
  const mutations = data?.mutations;
  const health = data?.health;
  const findings = data?.findings;

  // Calculate disk free bytes if not directly provided
  let diskFreeBytes = storage?.disk_free_bytes;
  if (
    diskFreeBytes == null &&
    storage?.disk_total_bytes != null &&
    storage?.disk_used_bytes != null
  ) {
    diskFreeBytes = Math.max(0, storage.disk_total_bytes - storage.disk_used_bytes);
  }

  const tableDataBytes = storage?.table_data_bytes ?? storage?.total_table_bytes;
  const sizeOnDiskBytes =
    storage?.size_on_disk_bytes ?? storage?.total_table_bytes ?? storage?.disk_used_bytes;
  const compressionRatio = formatCompression(storage);

  const isNearFull =
    storage?.disk_capacity_status === 'NEAR_FULL' ||
    (storage?.disk_used_pct != null && storage.disk_used_pct >= 90);

  return (
    <div className="space-y-6">
      {/* Top Banner Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-gray-200 dark:border-gray-800">
        <div className="flex items-center gap-3">
          <div className="p-1.5 rounded-lg bg-orange-500 text-white shadow-sm">
            <HardDrive className="w-4 h-4" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white leading-tight">
                Storage and Parts
              </h2>
              <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-amber-50 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-500/30">
                {(dbType || 'ClickHouse').toUpperCase()}
              </span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              ClickHouse storage engine, MergeTree parts, mutations, and disks telemetry
            </p>
          </div>
        </div>

        {/* Right Controls: Health Badge & Manual Refresh */}
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          {findings?.count != null && findings.count > 0 && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border shadow-sm bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-300 dark:border-amber-500/30">
              <Sparkles className="w-3.5 h-3.5 text-amber-500" />
              {findings.count} {findings.count === 1 ? 'Finding' : 'Findings'}
            </span>
          )}

          {health?.status && (
            <span
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border shadow-sm ${
                health.status === 'WARNING'
                  ? 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-300 dark:border-amber-500/30'
                  : health.status === 'CRITICAL'
                  ? 'bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-300 dark:border-rose-500/30'
                  : 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-300 dark:border-emerald-500/30'
              }`}
            >
              <span
                className={`w-2 h-2 rounded-full ${
                  health.status === 'WARNING'
                    ? 'bg-amber-500'
                    : health.status === 'CRITICAL'
                    ? 'bg-rose-500'
                    : 'bg-emerald-500 animate-pulse'
                }`}
              />
              {health.status}
            </span>
          )}

          {lastUpdated && (
            <span className="text-[11px] text-gray-400 dark:text-gray-500 hidden sm:inline">
              Updated {lastUpdated}
            </span>
          )}

          <button
            onClick={() => fetchData()}
            disabled={loading}
            className="p-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-700 transition-colors shadow-sm cursor-pointer disabled:opacity-50"
            title="Refresh Storage & Parts"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Warnings & Alerts Banner */}
      {health?.warnings && health.warnings.length > 0 && (
        <div className="space-y-2">
          {health.warnings.map((warn, index) => {
            const isSevere =
              warn.severity === 'NEAR_FULL' ||
              warn.severity === 'CRITICAL' ||
              warn.severity === 'DANGER';

            return (
              <div
                key={index}
                className={`p-3.5 rounded-xl border flex items-start gap-3 transition-all ${
                  isSevere
                    ? 'bg-rose-50 dark:bg-rose-950/30 border-rose-200 dark:border-rose-800/50'
                    : 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800/50'
                }`}
              >
                <AlertTriangle
                  className={`w-4 h-4 shrink-0 mt-0.5 ${
                    isSevere
                      ? 'text-rose-600 dark:text-rose-400'
                      : 'text-amber-600 dark:text-amber-400'
                  }`}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                    <span
                      className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${
                        isSevere
                          ? 'bg-rose-100 dark:bg-rose-900/50 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-700/50'
                          : 'bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-700/50'
                      }`}
                    >
                      {warn.severity || 'WARNING'}
                    </span>
                    {warn.code && (
                      <span className="text-[11px] font-mono text-gray-500 dark:text-gray-400">
                        [{warn.code}]
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-800 dark:text-gray-200 mt-1 leading-relaxed">
                    {warn.message}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Loading Skeleton */}
      {loading && !data && (
        <div className="flex flex-col items-center justify-center p-12 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl min-h-[260px]">
          <Loader2 className="w-8 h-8 text-orange-500 animate-spin mb-3" />
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Loading ClickHouse storage and parts overview...
          </p>
        </div>
      )}

      {/* Error State */}
      {!loading && error && (
        <div className="p-6 bg-rose-50 dark:bg-rose-950/20 border border-rose-200 dark:border-rose-800/40 rounded-xl text-center">
          <AlertTriangle className="w-8 h-8 text-rose-500 mx-auto mb-2" />
          <h3 className="text-sm font-bold text-gray-900 dark:text-white">
            Unable to fetch Storage & Parts data
          </h3>
          <p className="text-xs text-rose-600 dark:text-rose-400 mt-1 max-w-md mx-auto">
            {error}
          </p>
          <button
            onClick={() => fetchData()}
            className="mt-3 px-3.5 py-1.5 bg-rose-600 text-white rounded-lg text-xs font-semibold hover:bg-rose-700 transition-colors cursor-pointer"
          >
            Retry Fetch
          </button>
        </div>
      )}

      {/* 9 Compact KPI Stat Cards with Icons */}
      {data && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-5 gap-2 sm:gap-2.5">
            {/* Card 1: Disk used */}
            <div
              className="bg-white dark:bg-gray-900/90 border border-gray-200 dark:border-gray-800 rounded-xl p-2.5 sm:p-3 shadow-sm hover:border-orange-500/30 transition-all flex flex-col justify-between"
              title={`Disk Used: ${formatPercent(storage?.disk_used_pct)} (Host: ${formatPercent(storage?.host_disk_used_pct)})`}
            >
              <div className="flex items-center justify-between gap-1 mb-1">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 truncate">
                  Disk used
                </span>
                <div className="p-1 rounded-md bg-orange-500/10 text-orange-600 dark:text-orange-400 border border-orange-500/20 shrink-0">
                  <HardDrive className="w-3.5 h-3.5" />
                </div>
              </div>
              <div>
                <div
                  className={`text-base sm:text-lg font-bold font-mono leading-tight truncate ${
                    isNearFull
                      ? 'text-red-600 dark:text-red-400'
                      : 'text-gray-900 dark:text-white'
                  }`}
                >
                  {formatPercent(storage?.disk_used_pct)}
                </div>
                <div className="text-[10px] text-gray-400 dark:text-gray-500 truncate mt-0.5">
                  {storage?.disk_capacity_status || 'Capacity'}
                </div>
              </div>
            </div>

            {/* Card 2: Disk free */}
            <div
              className="bg-white dark:bg-gray-900/90 border border-gray-200 dark:border-gray-800 rounded-xl p-2.5 sm:p-3 shadow-sm hover:border-emerald-500/30 transition-all flex flex-col justify-between"
              title={`Disk Free: ${formatBytes(diskFreeBytes)} (Total: ${formatBytes(storage?.disk_total_bytes)})`}
            >
              <div className="flex items-center justify-between gap-1 mb-1">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 truncate">
                  Disk free
                </span>
                <div className="p-1 rounded-md bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 shrink-0">
                  <Database className="w-3.5 h-3.5" />
                </div>
              </div>
              <div>
                <div className="text-base sm:text-lg font-bold font-mono text-gray-900 dark:text-white leading-tight truncate">
                  {formatBytes(diskFreeBytes)}
                </div>
                <div className="text-[10px] text-gray-400 dark:text-gray-500 truncate mt-0.5">
                  {storage?.disk_total_bytes ? `of ${formatBytes(storage.disk_total_bytes)}` : 'Available'}
                </div>
              </div>
            </div>

            {/* Card 3: Table data */}
            <div
              className="bg-white dark:bg-gray-900/90 border border-gray-200 dark:border-gray-800 rounded-xl p-2.5 sm:p-3 shadow-sm hover:border-cyan-500/30 transition-all flex flex-col justify-between"
              title={`Table Data: ${formatBytes(tableDataBytes)} (Uncompressed)`}
            >
              <div className="flex items-center justify-between gap-1 mb-1">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 truncate">
                  Table data
                </span>
                <div className="p-1 rounded-md bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border border-cyan-500/20 shrink-0">
                  <Layers className="w-3.5 h-3.5" />
                </div>
              </div>
              <div>
                <div className="text-base sm:text-lg font-bold font-mono text-gray-900 dark:text-white leading-tight truncate">
                  {formatBytes(tableDataBytes)}
                </div>
                <div className="text-[10px] text-gray-400 dark:text-gray-500 truncate mt-0.5">
                  {storage?.tables_fresh ? 'Fresh' : 'Data stale'}
                </div>
              </div>
            </div>

            {/* Card 4: Total rows */}
            <div
              className="bg-white dark:bg-gray-900/90 border border-gray-200 dark:border-gray-800 rounded-xl p-2.5 sm:p-3 shadow-sm hover:border-violet-500/30 transition-all flex flex-col justify-between"
              title={`Total Rows: ${formatNumber(storage?.total_table_rows)}`}
            >
              <div className="flex items-center justify-between gap-1 mb-1">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 truncate">
                  Total rows
                </span>
                <div className="p-1 rounded-md bg-violet-500/10 text-violet-600 dark:text-violet-400 border border-violet-500/20 shrink-0">
                  <Hash className="w-3.5 h-3.5" />
                </div>
              </div>
              <div>
                <div className="text-base sm:text-lg font-bold font-mono text-gray-900 dark:text-white leading-tight truncate">
                  {formatNumber(storage?.total_table_rows)}
                </div>
                <div className="text-[10px] text-gray-400 dark:text-gray-500 truncate mt-0.5">
                  MergeTree
                </div>
              </div>
            </div>

            {/* Card 5: Active parts */}
            <div
              className="bg-white dark:bg-gray-900/90 border border-gray-200 dark:border-gray-800 rounded-xl p-2.5 sm:p-3 shadow-sm hover:border-blue-500/30 transition-all flex flex-col justify-between"
              title={`Active Parts: ${formatNumber(parts?.active_parts)}`}
            >
              <div className="flex items-center justify-between gap-1 mb-1">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 truncate">
                  Active parts
                </span>
                <div className="p-1 rounded-md bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20 shrink-0">
                  <Boxes className="w-3.5 h-3.5" />
                </div>
              </div>
              <div>
                <div className="text-base sm:text-lg font-bold font-mono text-gray-900 dark:text-white leading-tight truncate">
                  {formatNumber(parts?.active_parts)}
                </div>
                <div className="text-[10px] text-gray-400 dark:text-gray-500 truncate mt-0.5">
                  {parts?.parts_fresh ? 'Fresh' : 'Stale'}
                </div>
              </div>
            </div>

            {/* Card 6: Size on disk */}
            <div
              className="bg-white dark:bg-gray-900/90 border border-gray-200 dark:border-gray-800 rounded-xl p-2.5 sm:p-3 shadow-sm hover:border-purple-500/30 transition-all flex flex-col justify-between"
              title={`Size on Disk: ${formatBytes(sizeOnDiskBytes)} (Compressed)`}
            >
              <div className="flex items-center justify-between gap-1 mb-1">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 truncate">
                  Size on disk
                </span>
                <div className="p-1 rounded-md bg-purple-500/10 text-purple-600 dark:text-purple-400 border border-purple-500/20 shrink-0">
                  <Archive className="w-3.5 h-3.5" />
                </div>
              </div>
              <div>
                <div className="text-base sm:text-lg font-bold font-mono text-gray-900 dark:text-white leading-tight truncate">
                  {formatBytes(sizeOnDiskBytes)}
                </div>
                <div className="text-[10px] text-gray-400 dark:text-gray-500 truncate mt-0.5">
                  Compressed
                </div>
              </div>
            </div>

            {/* Card 7: Compression */}
            <div
              className="bg-white dark:bg-gray-900/90 border border-gray-200 dark:border-gray-800 rounded-xl p-2.5 sm:p-3 shadow-sm hover:border-emerald-500/30 transition-all flex flex-col justify-between"
              title={`Compression Ratio: ${compressionRatio}`}
            >
              <div className="flex items-center justify-between gap-1 mb-1">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 truncate">
                  Compression
                </span>
                <div className="p-1 rounded-md bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 shrink-0">
                  <Minimize2 className="w-3.5 h-3.5" />
                </div>
              </div>
              <div>
                <div className="text-base sm:text-lg font-bold font-mono text-gray-900 dark:text-white leading-tight truncate">
                  {compressionRatio}
                </div>
                <div className="text-[10px] text-gray-400 dark:text-gray-500 truncate mt-0.5">
                  Savings ratio
                </div>
              </div>
            </div>

            {/* Card 8: Running merges */}
            <div
              className="bg-white dark:bg-gray-900/90 border border-gray-200 dark:border-gray-800 rounded-xl p-2.5 sm:p-3 shadow-sm hover:border-amber-500/30 transition-all flex flex-col justify-between"
              title={`Running Merges: ${formatNumber(merges?.running_count)} (Longest: ${merges?.longest_elapsed_sec != null ? `${merges.longest_elapsed_sec}s` : '0s'})`}
            >
              <div className="flex items-center justify-between gap-1 mb-1">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 truncate">
                  Running merges
                </span>
                <div className="p-1 rounded-md bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20 shrink-0">
                  <GitMerge className="w-3.5 h-3.5" />
                </div>
              </div>
              <div>
                <div className="text-base sm:text-lg font-bold font-mono text-gray-900 dark:text-white leading-tight truncate">
                  {formatNumber(merges?.running_count)}
                </div>
                <div className="text-[10px] text-gray-400 dark:text-gray-500 truncate mt-0.5">
                  {merges?.longest_elapsed_sec != null ? `${merges.longest_elapsed_sec}s max` : 'Idle'}
                </div>
              </div>
            </div>

            {/* Card 9: Detached parts */}
            <div
              className="bg-white dark:bg-gray-900/90 border border-gray-200 dark:border-gray-800 rounded-xl p-2.5 sm:p-3 shadow-sm hover:border-slate-500/30 transition-all flex flex-col justify-between"
              title={`Detached Parts: ${formatNumber(parts?.detached_parts)}`}
            >
              <div className="flex items-center justify-between gap-1 mb-1">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 truncate">
                  Detached parts
                </span>
                <div
                  className={`p-1 rounded-md border shrink-0 ${
                    (parts?.detached_parts || 0) > 0
                      ? 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20'
                      : 'bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/20'
                  }`}
                >
                  <Unlink className="w-3.5 h-3.5" />
                </div>
              </div>
              <div>
                <div className="text-base sm:text-lg font-bold font-mono text-gray-900 dark:text-white leading-tight truncate">
                  {formatNumber(parts?.detached_parts)}
                </div>
                <div className="text-[10px] text-gray-400 dark:text-gray-500 truncate mt-0.5">
                  {(parts?.detached_parts || 0) > 0 ? 'Requires check' : 'Healthy'}
                </div>
              </div>
            </div>
          </div>

          {/* Detailed Storage & Pipeline Breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Box 1: Storage Capacity & Disks Telemetry */}
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4 shadow-sm space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Gauge className="w-4 h-4 text-orange-500" />
                  <h3 className="text-sm font-bold text-gray-900 dark:text-white">
                    Disk Storage & Capacity
                  </h3>
                </div>
                {storage?.disk_capacity_status && (
                  <span
                    className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                      storage.disk_capacity_status === 'NEAR_FULL'
                        ? 'bg-red-500/15 text-red-600 dark:text-red-400 border border-red-500/30'
                        : storage.disk_capacity_status === 'CRITICAL'
                        ? 'bg-rose-500/15 text-rose-600 dark:text-rose-400 border border-rose-500/30'
                        : 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30'
                    }`}
                  >
                    {storage.disk_capacity_status}
                  </span>
                )}
              </div>

              {/* Progress bar */}
              <div>
                <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400 mb-1.5 font-medium">
                  <span>Disk Utilization</span>
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {formatPercent(storage?.disk_used_pct)}
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-800 rounded-full h-2.5 overflow-hidden">
                  <div
                    className={`h-2.5 rounded-full transition-all duration-500 ${
                      (storage?.disk_used_pct || 0) >= 90
                        ? 'bg-red-500'
                        : (storage?.disk_used_pct || 0) >= 95
                        ? 'bg-rose-500'
                        : 'bg-red-500'
                    }`}
                    style={{ width: `${Math.min(100, Math.max(0, storage?.disk_used_pct || 0))}%` }}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 pt-2 border-t border-gray-100 dark:border-gray-800/80 text-xs">
                <div>
                  <span className="text-[11px] text-gray-400">Disk Used:</span>
                  <div className="font-semibold text-gray-800 dark:text-gray-200 mt-0.5">
                    {formatBytes(storage?.disk_used_bytes)}
                  </div>
                </div>
                <div>
                  <span className="text-[11px] text-gray-400">Disk Total:</span>
                  <div className="font-semibold text-gray-800 dark:text-gray-200 mt-0.5">
                    {formatBytes(storage?.disk_total_bytes)}
                  </div>
                </div>
                <div>
                  <span className="text-[11px] text-gray-400">Host Disk Used:</span>
                  <div className="font-semibold text-gray-800 dark:text-gray-200 mt-0.5">
                    {storage?.host_disk_used_pct != null
                      ? `${Number(storage.host_disk_used_pct).toFixed(2)}%`
                      : '—'}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4 pt-1 text-[11px] text-gray-500 dark:text-gray-400">
                <span className="flex items-center gap-1.5">
                  <span
                    className={`w-2 h-2 rounded-full ${
                      storage?.disks_fresh ? 'bg-emerald-500' : 'bg-gray-400'
                    }`}
                  />
                  Disks Telemetry: {storage?.disks_fresh ? 'Fresh' : 'Stale'}
                </span>
                <span className="flex items-center gap-1.5">
                  <span
                    className={`w-2 h-2 rounded-full ${
                      storage?.tables_fresh ? 'bg-emerald-500' : 'bg-gray-400'
                    }`}
                  />
                  Tables Telemetry: {storage?.tables_fresh ? 'Fresh' : 'Stale'}
                </span>
              </div>
            </div>

            {/* Box 2: MergeTree Merges & Mutations Pipeline */}
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4 shadow-sm space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-amber-500" />
                  <h3 className="text-sm font-bold text-gray-900 dark:text-white">
                    Parts, Merges & Mutations Pipeline
                  </h3>
                </div>
                <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                  MergeTree Engine
                </span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                <div className="bg-gray-50 dark:bg-gray-800/40 p-2.5 rounded-lg border border-gray-200/50 dark:border-gray-800/60">
                  <span className="text-[11px] text-gray-400">Running Merges</span>
                  <div className="text-base font-bold text-gray-900 dark:text-white mt-0.5 font-mono">
                    {formatNumber(merges?.running_count)}
                  </div>
                  <span className="text-[10px] text-gray-400">
                    Longest:{' '}
                    {merges?.longest_elapsed_sec != null
                      ? `${merges.longest_elapsed_sec}s`
                      : '0s'}
                  </span>
                </div>

                <div className="bg-gray-50 dark:bg-gray-800/40 p-2.5 rounded-lg border border-gray-200/50 dark:border-gray-800/60">
                  <span className="text-[11px] text-gray-400">Pending Mutations</span>
                  <div className="text-base font-bold text-gray-900 dark:text-white mt-0.5 font-mono">
                    {formatNumber(mutations?.pending_count)}
                  </div>
                  <span className="text-[10px] text-gray-400">
                    {mutations?.fresh ? 'Fresh pipeline' : 'Data stale'}
                  </span>
                </div>

                <div className="bg-gray-50 dark:bg-gray-800/40 p-2.5 rounded-lg border border-gray-200/50 dark:border-gray-800/60">
                  <span className="text-[11px] text-gray-400">Failed Mutations</span>
                  <div
                    className={`text-base font-bold mt-0.5 font-mono ${
                      (mutations?.failed_count || 0) > 0
                        ? 'text-rose-500'
                        : 'text-gray-900 dark:text-white'
                    }`}
                  >
                    {formatNumber(mutations?.failed_count)}
                  </div>
                  <span className="text-[10px] text-gray-400">
                    {(mutations?.failed_count || 0) > 0 ? 'Requires action' : 'None reported'}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-4 pt-1 text-[11px] text-gray-500 dark:text-gray-400 border-t border-gray-100 dark:border-gray-800/80">
                <span className="flex items-center gap-1.5">
                  <span
                    className={`w-2 h-2 rounded-full ${
                      parts?.parts_fresh ? 'bg-emerald-500' : 'bg-gray-400'
                    }`}
                  />
                  Parts: {parts?.parts_fresh ? 'Fresh' : 'Stale'}
                </span>
                <span className="flex items-center gap-1.5">
                  <span
                    className={`w-2 h-2 rounded-full ${
                      merges?.fresh ? 'bg-emerald-500' : 'bg-gray-400'
                    }`}
                  />
                  Merges: {merges?.fresh ? 'Fresh' : 'Stale'}
                </span>
                <span className="flex items-center gap-1.5">
                  <span
                    className={`w-2 h-2 rounded-full ${
                      mutations?.fresh ? 'bg-emerald-500' : 'bg-gray-400'
                    }`}
                  />
                  Mutations: {mutations?.fresh ? 'Fresh' : 'Stale'}
                </span>
              </div>
            </div>
          </div>

          {/* Automated Findings & Recommendations */}
          {findings && (
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4 sm:p-5 shadow-sm space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-3 border-b border-gray-100 dark:border-gray-800">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 rounded-lg bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20">
                    <Sparkles className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
                      Automated Findings & Recommendations
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300">
                        {findings.count ?? findings.items?.length ?? 0}
                      </span>
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      Proactive storage volume, MergeTree parts, and pipeline diagnostics from ClickHouse monitoring agent
                    </p>
                  </div>
                </div>

                {findings.by_severity && (
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {Object.entries(findings.by_severity).map(([sev, count]) => {
                      const color =
                        sev === 'critical'
                          ? 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20'
                          : sev === 'warning'
                            ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20'
                            : 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20';
                      return (
                        <span
                          key={sev}
                          className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${color}`}
                        >
                          {count} {sev}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Advisory Note */}
              {findings.note && (
                <div className="p-3.5 rounded-xl bg-amber-500/10 border border-amber-500/20 text-xs text-amber-800 dark:text-amber-200 flex items-start gap-2.5">
                  <Info className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div className="space-y-1">
                    <span className="font-semibold text-amber-900 dark:text-amber-100">Advisory Notice:</span>
                    <p className="leading-relaxed text-[11px] opacity-90">{findings.note}</p>
                  </div>
                </div>
              )}

              {/* Findings Cards List */}
              {(!findings.items || findings.items.length === 0) ? (
                <div className="py-8 text-center text-xs text-gray-500 dark:text-gray-400">
                  No automated findings reported.
                </div>
              ) : (
                <div className="space-y-3">
                  {findings.items.map((item, idx) => {
                    const sevColor =
                      item.severity === 'critical'
                        ? 'bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-400 border-rose-200 dark:border-rose-500/30'
                        : item.severity === 'warning'
                          ? 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400 border-amber-200 dark:border-amber-500/30'
                          : 'bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-400 border-blue-200 dark:border-blue-500/30';

                    return (
                      <div
                        key={idx}
                        className="p-4 rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/50 hover:border-gray-300 dark:hover:border-gray-700 transition-all space-y-2.5"
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <span
                              className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${sevColor}`}
                            >
                              {item.severity}
                            </span>
                            <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-gray-200/70 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                              {item.category?.replace(/_/g, ' ')}
                            </span>
                            {item.database && (
                              <span className="px-2 py-0.5 rounded text-[10px] font-mono font-medium bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-500/20">
                                {item.database}
                              </span>
                            )}
                          </div>
                          {item.object && (
                            <span className="font-mono text-xs font-semibold text-gray-700 dark:text-gray-300 truncate max-w-md">
                              {item.object}
                            </span>
                          )}
                        </div>

                        <div className="space-y-1.5 text-xs">
                          <div className="text-gray-900 dark:text-white font-medium leading-relaxed">
                            {item.finding}
                          </div>
                          {item.impact && (
                            <div className="text-gray-600 dark:text-gray-300 text-[11px] flex items-start gap-1.5">
                              <span className="font-semibold text-gray-500 dark:text-gray-400 min-w-[55px]">
                                Impact:
                              </span>
                              <span>{item.impact}</span>
                            </div>
                          )}
                          {item.remediation && (
                            <div className="text-gray-600 dark:text-gray-300 text-[11px] flex items-start gap-1.5">
                              <span className="font-semibold text-emerald-600 dark:text-emerald-400 min-w-[55px]">
                                Remedy:
                              </span>
                              <span>{item.remediation}</span>
                            </div>
                          )}
                        </div>

                        {item.command && (
                          <div className="pt-1">
                            <div className="flex items-center justify-between bg-gray-900 text-gray-100 dark:bg-black/60 rounded-lg p-2.5 font-mono text-[11px] border border-gray-700/60 overflow-x-auto">
                              <div className="flex items-center gap-2 flex-1 min-w-0 pr-2">
                                <Terminal className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                                <span className="truncate select-all">{item.command}</span>
                              </div>
                              <button
                                onClick={() => handleCopy(item.command || '')}
                                className="px-2 py-1 rounded bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white transition-colors flex items-center gap-1 text-[10px] font-medium flex-shrink-0 cursor-pointer"
                                title="Copy command to clipboard"
                              >
                                {copiedCommand === item.command ? (
                                  <>
                                    <Check className="w-3 h-3 text-emerald-400" />
                                    <span className="text-emerald-400">Copied</span>
                                  </>
                                ) : (
                                  <>
                                    <Copy className="w-3 h-3" />
                                    <span>Copy</span>
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ClickHouse Storage Tables */}
          <ClickhouseTable
            activeCluster={activeCluster}
            timeRange={timeRange}
            clientId={clientId}
            refreshKey={refreshKey}
            dbType={dbType}
          />

          {/* ClickHouse Parts */}
          <ClickhouseParts
            activeCluster={activeCluster}
            timeRange={timeRange}
            clientId={clientId}
            refreshKey={refreshKey}
            dbType={dbType}
          />

          {/* ClickHouse Merges */}
          <ClickhouseMerges
            activeCluster={activeCluster}
            timeRange={timeRange}
            clientId={clientId}
            refreshKey={refreshKey}
            dbType={dbType}
          />
        </div>
      )}
    </div>
  );
};

export const ClickhouseStorageView = ClickhouseStorage;
export default ClickhouseStorage;
